import {gql} from '@apollo/client';
import React, {useEffect, useState} from 'react';
import {v4 as uuid4} from 'uuid';

import {assert} from 'Asserts';
import * as BenchlingUtil from 'BenchlingUtil';
import FolderSelector from 'file-browser/FolderSelector';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import {ActionMethod, ActionOrigin, NameTemplateType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import notifications from 'notifications';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  createRequestV2SubmissionEventMetadata,
  REQUESTS_V2_CREATE_REQUEST_V2_SUBMISSION_STARTED_EVENT,
  REQUESTS_V2_CREATE_REQUEST_V2_SUBMISSION_ENDED_EVENT,
} from 'performance/processRequestsV2Events';
import {CreateRequestV2SubmissionModalQuery} from 'processes/requests_v2/modals/__generated/CreateRequestV2SubmissionModalQuery';
import {
  createRequestV2Submission,
  CreateRequestV2SubmissionResponse,
} from 'processes/requests_v2/network/RequestV2SubmissionDAO';
import {requestV2SubmissionPageUrl} from 'processes/requests_v2/network/RequestV2SubmissionDAO';
import {maybeStrLengthValidationMessage} from 'processes/requests_v2/request-v2-definition/RequestV2DefinitionUtils';
import {
  getRequestV2SubmissionDetailsFromApiResponse,
  writeRequestV2SubmissionUpdateToCache,
} from 'processes/requests_v2/request-v2-submission/RequestV2SubmissionDetailsQueryUtils';
import {ProcessTaskStructuredTable} from 'processes/tables/ProcessTaskStructuredTableAdapter';
import EntityStore from 'stores/EntityStore';
import StudySearchInput, {StudySearchItem} from 'studies/StudySearchInput';
import ModalButtonKey from 'test/lib/cypress/shared/ModalButtonKey';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import Label from 'ui-library/bs/Label';
import Row from 'ui-library/bs/Row';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {EntityWithTextAndSuspended} from 'ui-library/entity-input/EntityInputConstants';
import {EntityInputWithSearch} from 'ui-library/entity-input/EntityInputWithSearch';
import ErrorMessage from 'ui-library/ErrorMessage';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import TextField from 'ui-library/inputs/TextField';
import Menu from 'ui-library/menus/Menu';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import Modal from 'ui-library/modals/Modal';
import {TagsInputSize} from 'ui-library/tags-input/TagsInputConstants';
import {omit} from 'util/omitKey';

import {MAX_REQUEST_SUBMISSION_NAME_LENGTH} from '../request-v2-submission/RequestV2SubmissionUtils';
import useSeedTasks from './useSeedTasks';

export const CREATE_REQUEST_V2_SUBMISSION_MODAL_QUERY = gql`
  query CreateRequestV2SubmissionModalQuery @skipAudit @engComponent(name: WORKFLOWS) {
    allRequestV2Definitions {
      id
      name
      archiveData {
        archived
      }
      nameTemplates {
        templateType
        nameTemplateParts {
          type
        }
      }
      workflowTaskSchema {
        ... on ProcessTaskSchemaNode {
          id
          defaultCreationFolderOrInaccessible {
            ... on FolderNode {
              id
            }
            ...InaccessibleInfoGraphQL
          }
        }
        ...InaccessibleInfoGraphQL
      }
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export interface CreateRequestV2SubmissionModalProps {
  actionOrigin: ActionOrigin;
  initialRequestV2DefinitionId?: string;
  initialFolderId?: string;
  taskTableWithPropagatedContent?: ProcessTaskStructuredTable;
  onRequestClose: () => void;
}

type RequestV2Definition = ExtractByTypename<
  CreateRequestV2SubmissionModalQuery['allRequestV2Definitions'][number],
  'RequestV2DefinitionNode'
>;

export default function QueryingCreateRequestV2SubmissionModal(
  props: CreateRequestV2SubmissionModalProps
): JSX.Element | null {
  const requestV2DefinitionQueryResult = useBenchlingQuery<CreateRequestV2SubmissionModalQuery>(
    CREATE_REQUEST_V2_SUBMISSION_MODAL_QUERY,
    {
      fetchPolicy: 'cache-and-network',
    }
  );

  if (requestV2DefinitionQueryResult.type === QueryResultType.PENDING) {
    return (
      <Modal
        title="Create a new request"
        onRequestClose={props.onRequestClose}
        buttons={[{text: 'Create', submit: true, disabled: true}]}
        dataTestAttrs={{component: 'CreateRequestV2SubmissionModal', element: 'modal'}}
      >
        <CenteredHorizontalThrobber />
      </Modal>
    );
  } else if (requestV2DefinitionQueryResult.type === QueryResultType.ERROR) {
    notifications.error('Could not load request templates');
    return null;
  }

  return (
    <CreateRequestV2SubmissionModalBody
      {...props}
      requestV2Definitions={requestV2DefinitionQueryResult.data.allRequestV2Definitions}
    />
  );
}

function CreateRequestV2SubmissionModalBody(
  props: CreateRequestV2SubmissionModalProps & {requestV2Definitions: Array<RequestV2Definition>}
): JSX.Element {
  function getInitialFolder(): string | null {
    if (props.initialFolderId != null) {
      return props.initialFolderId;
    }
    return FolderDefaults.get(FolderDefaultsKey.REQUESTS_V2) ?? null;
  }

  const [requestV2Definition, setRequestV2Definition] = useState<RequestV2Definition | null>(
    props.initialRequestV2DefinitionId != null
      ? (props.requestV2Definitions.find((rd) => rd.id === props.initialRequestV2DefinitionId) ?? null)
      : null
  );
  const initialFolder = getInitialFolder();
  const [folderId, setFolderId] = useState<string | null>(initialFolder);
  const [name, setName] = useState<string | null>(null);
  const [watchers, setWatchers] = useState<Array<EntityWithTextAndSuspended>>([EntityStore.currentUser()]);
  const [shouldOverrideFolder, setShouldOverrideFolder] = useState<boolean>(true);
  const [studies, setStudies] = useState<Array<StudySearchItem>>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const {seedTasks, isLoading, footerMessage, dismissFooterMessage} = useSeedTasks(
    props.taskTableWithPropagatedContent
  );

  assert(
    (requestV2Definition?.nameTemplates?.length ?? 0) <= 1,
    'Expected at most one name template per request definition'
  );
  const requestV2NameTemplate = requestV2Definition?.nameTemplates?.find(
    (t) => t.templateType === NameTemplateType.REQUEST_V2
  );
  const hasAutoNaming = (requestV2NameTemplate?.nameTemplateParts?.length ?? 0) > 0;

  function isSubmitEnabled(): boolean {
    const hasRequestV2Definition = requestV2Definition != null;
    const hasFolderId = folderId != null;
    const hasName = name != null && name.trim().length > 0;
    return (
      hasRequestV2Definition &&
      hasFolderId &&
      (hasAutoNaming || hasName) &&
      (name == null || maybeStrLengthValidationMessage(name, MAX_REQUEST_SUBMISSION_NAME_LENGTH) == null)
    );
  }

  function checkLength(inputStr: string, propertyName: string, maxLength: number): void {
    const maybeErrorMessage = maybeStrLengthValidationMessage(inputStr, maxLength);

    if (maybeErrorMessage != null) {
      setErrors({
        ...errors,
        [propertyName]: maybeErrorMessage,
      });
    } else {
      setErrors({...omit(errors, propertyName)});
    }
  }

  useEffect(() => {
    if (shouldOverrideFolder) {
      if (requestV2Definition != null && !isInaccessible(requestV2Definition.workflowTaskSchema)) {
        const taskSchema = requestV2Definition.workflowTaskSchema;
        if (
          taskSchema.defaultCreationFolderOrInaccessible != null &&
          !isInaccessible(taskSchema.defaultCreationFolderOrInaccessible)
        ) {
          setFolderId(taskSchema.defaultCreationFolderOrInaccessible.id);
        } else if (taskSchema.defaultCreationFolderOrInaccessible == null) {
          setFolderId(initialFolder);
        }
      }
    }
  }, [requestV2Definition, shouldOverrideFolder, initialFolder]);

  async function handleSubmit(): Promise<void> {
    assert(
      folderId != null && requestV2Definition != null,
      'Submit button should be disabled if folder ID or request definition is not specified'
    );
    setErrors({});

    let response: CreateRequestV2SubmissionResponse | null = null;
    const profileEventId = uuid4();
    const profileEventMetadata = createRequestV2SubmissionEventMetadata(props.actionOrigin, seedTasks.length);
    logProfilerTimingEvent(
      {...REQUESTS_V2_CREATE_REQUEST_V2_SUBMISSION_STARTED_EVENT, id: profileEventId},
      profileEventMetadata
    );

    const submissionName = name != null && name.trim().length > 0 ? name.trim() : undefined;
    try {
      response = await createRequestV2Submission({
        folderId,
        name: submissionName,
        requestV2DefinitionId: requestV2Definition.id,
        actionOrigin: props.actionOrigin,
        actionMethod: ActionMethod.CREATE_MODAL,
        studyIds: studies.map((study) => study.id),
        watcherIds: watchers.map((watcher) => watcher.id),
        taskSpecs: seedTasks,
      });

      assert(
        response.requestV2Submissions.length === 1,
        'Expected exactly one RequestV2Submission to be created'
      );

      props.onRequestClose();
    } catch (e) {
      if (e.responseJSON?.errors != null) {
        setErrors(e.responseJSON.errors);
      } else {
        throw e;
      }
    }

    if (response != null) {
      logProfilerTimingEvent(
        {...REQUESTS_V2_CREATE_REQUEST_V2_SUBMISSION_ENDED_EVENT, id: profileEventId},
        profileEventMetadata
      );
      FolderDefaults.set(FolderDefaultsKey.REQUESTS_V2, folderId);
      writeRequestV2SubmissionUpdateToCache(
        getRequestV2SubmissionDetailsFromApiResponse(response.requestV2Submissions[0])
      );
      BenchlingUtil.navigate(null, requestV2SubmissionPageUrl(response.requestV2Submissions[0].id));
    }
  }

  const enableSubmit = isSubmitEnabled();

  return (
    <Modal
      title="Create a new request"
      onRequestClose={props.onRequestClose}
      onRequestSubmit={handleSubmit}
      buttons={[
        {
          text: 'Next',
          dataTestAttrs: {
            component: 'CreateRequestV2SubmissionModal',
            element: 'nextButton',
            key: ModalButtonKey.NEXT,
          },
          submit: true,
          disabled: !enableSubmit,
          tooltipTitle: enableSubmit ? null : 'Complete all required fields to create your request',
        },
      ]}
      dataTestAttrs={{component: 'CreateRequestV2SubmissionModal', element: 'modal'}}
    >
      {isLoading ? (
        <CenteredHorizontalThrobber />
      ) : (
        <>
          <Row>
            <Col lg={12}>
              <FieldLabel isRequired={true} label="Select a request type" />
              <RequestV2TemplateDropdown
                value={requestV2Definition}
                onSelect={(newRequestV2Definition) => {
                  if (newRequestV2Definition !== requestV2Definition) {
                    setRequestV2Definition(newRequestV2Definition);
                  }
                }}
                requestV2Definitions={props.requestV2Definitions}
                disabled={props.initialRequestV2DefinitionId != null}
              />
            </Col>
          </Row>
          {footerMessage != null ? (
            <Row>
              <Col lg={12}>
                <InlineBanner
                  bannerType={InlineBannerType.WARNING}
                  text={footerMessage.header}
                  supportingInfo={footerMessage.supportingInfo}
                  onDismiss={dismissFooterMessage}
                />
              </Col>
            </Row>
          ) : null}
          <Row>
            <Col sm={6}>
              <NameField
                value={name ?? ''}
                error={errors.name}
                isRequired={!hasAutoNaming}
                placeholder={hasAutoNaming ? 'Auto-generated from template' : undefined}
                onChange={(value) => {
                  setName(value);
                  checkLength(value, 'name', MAX_REQUEST_SUBMISSION_NAME_LENGTH);
                }}
              />
            </Col>
            <Col sm={6}>
              <FolderField
                folderId={folderId}
                error={errors.folder_id}
                onChange={(selectedFolderId) => {
                  setFolderId(selectedFolderId);
                  setShouldOverrideFolder(false);
                  setErrors({...omit(errors, 'folder_id')});
                }}
              />
            </Col>
          </Row>
          <Row>
            <Col sm={6}>
              <FieldLabel label="Studies" />
              <StudySearchInput
                selectedItems={studies}
                onChange={(studies) => {
                  setStudies(studies);
                }}
                shouldAllowMultiple={true}
              />
            </Col>
            <Col sm={6}>
              <Label>Watchers</Label>
              <EntityInputWithSearch
                value={watchers}
                placeholder="Set watchers"
                size={TagsInputSize.SMALL}
                onChange={setWatchers}
                allowEmails={true}
                allowTeams={true}
                allowOrganizations={false}
                shouldAllowMultiple={true}
                enableNewEntityInput={true}
              />
            </Col>
          </Row>
        </>
      )}
    </Modal>
  );
}

function NameField(props: {
  value: string;
  error: string | undefined;
  isRequired: boolean;
  placeholder?: string;
  onChange: (value: string) => void;
}): JSX.Element {
  return (
    <FormGroup>
      <TextField
        label="Name your request"
        value={props.value}
        placeholder={props.placeholder}
        onChange={(e) => {
          props.onChange(e.target.value);
        }}
        isRequired={props.isRequired}
        dataTestAttrs={{component: 'CreateRequestV2SubmissionModal', element: 'name'}}
      />
      <ErrorMessage
        error={props.error}
        dataTestAttrs={{
          component: 'CreateRequestV2SubmissionModal',
          element: 'nameFieldErrorMessage',
        }}
      />
    </FormGroup>
  );
}

function FolderField(props: {
  folderId: string | null;
  error: string | undefined;
  onChange: (folderId: string) => void;
}): JSX.Element {
  return (
    <>
      <FieldLabel isRequired={true} label="Project" />
      <FolderSelector
        enableRedesign={true}
        showInventory={false}
        showNotebook={true}
        value={props.folderId}
        error={props.folderId == null ? 'Required' : props.error}
        onChange={(e) => {
          props.onChange(e.target.value);
        }}
        unsetOnFetchFail={true}
      />
      <ErrorMessage
        error={props.error}
        dataTestAttrs={{
          component: 'CreateRequestV2SubmissionModal',
          element: 'folderIdFieldErrorMessage',
        }}
      />
    </>
  );
}

export function RequestV2TemplateDropdown(props: {
  value: RequestV2Definition | null;
  onSelect: (value: RequestV2Definition) => void;
  requestV2Definitions: Array<RequestV2Definition>;
  disabled: boolean;
}): JSX.Element | null {
  return (
    <SelectDropdown
      displayName={props.value?.name ?? ''}
      disabled={props.disabled}
      menu={
        <SearchableMenu
          width={Menu.WIDTHS.FULL}
          optionDatas={props.requestV2Definitions
            .filter((requestV2Definition) => !requestV2Definition.archiveData.archived)
            .map((requestV2Definition) => ({
              name: requestV2Definition.name,
              value: requestV2Definition,
            }))}
          onSelect={props.onSelect}
          scrollable={true}
        />
      }
      dataTestAttrs={{component: 'RequestV2TemplateDropdown', element: 'selectDropdown'}}
    />
  );
}
